import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// routes
import { paths } from 'src/routes/paths';
// components
import SvgColor from 'src/components/svg-color';
import { useAuthContext } from '../../auth/hooks';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { user } = useAuthContext();
  const { t } = useTranslation();

  const data = useMemo(
    () => {
      // Configuration de base accessible à tous les utilisateurs
      const baseNavigation = [
        {
          subheader: t('app.name'),
          items: [
            { title: t('nav.home'), path: paths.home, icon: ICONS.dashboard },
            { title: t('nav.poets'), path: paths.poets, icon: ICONS.user },
            { title: t('nav.poems'), path: paths.poems, icon: ICONS.file },
            { title: t('nav.statistics'), path: paths.statistics, icon: ICONS.analytics },
          //  { title: t('nav.audios'), path: paths.audios, icon: ICONS.banking },
          ],
        }
      ];

      // Configuration administrative - accessible uniquement aux administrateurs
      const adminNavigation = {
        subheader: t('nav.admin.title'),
        items: [
          // Groupe Contenu
          {
            title: t('nav.admin.content'),
            path: '#',
            icon: ICONS.file,
            children: [
              {
                title: t('nav.admin.poems.title'),
                path: paths.dashboard.poems.root,
                icon: ICONS.file,
                children: [
                  { title: t('nav.admin.poems.list'), path: paths.dashboard.poems.list },
                  { title: t('nav.admin.poems.new'), path: paths.dashboard.poems.new },
                  { title: t('nav.admin.poems.vttGenerator'), path: paths.dashboard.poems.vttGenerator },
                ],
              },
              {
                title: t('nav.admin.difficultWords.title'),
                path: paths.dashboard.difficultWords.root,
                icon: ICONS.label,
                children: [
                  { title: t('nav.admin.difficultWords.list'), path: paths.dashboard.difficultWords.list },
                  { title: t('nav.admin.difficultWords.new'), path: paths.dashboard.difficultWords.new },
                ],
              },
              {
                title: t('nav.admin.audios.title'),
                path: paths.dashboard.audios.root,
                icon: ICONS.banking,
                children: [
                  { title: t('nav.admin.audios.list'), path: paths.dashboard.audios.list },
                  { title: t('nav.admin.audios.new'), path: paths.dashboard.audios.new },
                ],
              },
            ],
          },

          // Groupe Personnes
          {
            title: t('nav.admin.people'),
            path: '#',
            icon: ICONS.user,
            children: [
              {
                title: t('nav.admin.poets.title'),
                path: paths.dashboard.poets.root,
                icon: ICONS.user,
                children: [
                  { title: t('nav.admin.poets.list'), path: paths.dashboard.poets.list },
                  { title: t('nav.admin.poets.new'), path: paths.dashboard.poets.new },
                ],
              },
              {
                title: t('nav.admin.singers.title'),
                path: paths.dashboard.singers.root,
                icon: ICONS.user,
                children: [
                  { title: t('nav.admin.singers.list'), path: paths.dashboard.singers.list },
                  { title: t('nav.admin.singers.new'), path: paths.dashboard.singers.new },
                ],
              },
              {
                title: t('nav.admin.users.title'),
                path: paths.dashboard.users.root,
                icon: ICONS.user,
                children: [
                  { title: t('nav.admin.users.list'), path: paths.dashboard.users.list },
                  { title: t('nav.admin.users.new'), path: paths.dashboard.users.new },
                ],
              },
            ],
          },

          // Groupe Classification
          {
            title: t('nav.admin.classification'),
            path: '#',
            icon: ICONS.label,
            children: [
              {
                title: t('nav.admin.themes.title'),
                path: paths.dashboard.themes.root,
                icon: ICONS.label,
                children: [
                  { title: t('nav.admin.themes.list'), path: paths.dashboard.themes.list },
                  { title: t('nav.admin.themes.new'), path: paths.dashboard.themes.new },
                ],
              },
              {
                title: t('nav.admin.tags.title'),
                path: paths.dashboard.tags.root,
                icon: ICONS.label,
                children: [
                  { title: t('nav.admin.tags.list'), path: paths.dashboard.tags.list },
                  { title: t('nav.admin.tags.new'), path: paths.dashboard.tags.new },
                ],
              },
            ],
          },
        ],
      };

      // Vérifier si l'utilisateur est un administrateur
      if (user?.role === 'ADMIN') {
        return [...baseNavigation, adminNavigation];
      }

      // Retourner uniquement la navigation de base pour les utilisateurs non-admin
      return baseNavigation;
    },
    [user,t]
  );

  return data;
}
